import React, { useMemo, useState } from 'react';
import { Box, Collapse } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { StacklineDefaultProfileImage } from 'src/components/SvgIcons/SvgIcons';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';
import { SideDotsWithBar } from '../SideDotsWithBar';
import { EnhancedLibraryRecord, LibraryVersion } from '../types/libraryTypes';
import { MasterContentRow } from './MasterContentRow';
import { CurrentContentRow } from './CurrentContentRow';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { MasterContentRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';

type ContentItem = 
  | { type: 'version'; data: LibraryVersion & { name: string; profileName?: string; projectName?: string }; date: Date }
  | { type: 'master'; data: MasterContentRecord; date: Date }
  | { type: 'current'; data: MasterContentRecord; date: Date };
import { BEACON_TABS, BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { ContentProfileModal } from '../ContentProfileModal';
import { useQuery } from '@tanstack/react-query';
import { fetchUserMetaData } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/ViewAdjustmentModal/utils';
import { SectionTitle, DetailsRow, FieldGroup, FieldLabel, FieldValue, StatusGroup } from './LibraryTable.styles';
import ChevronRight from '!svg-react-loader!src/components/BeaconV2Components/Pagination/assets/chevronRight.svg';
import { getInitials } from 'src/utils/stringFormatting';
import { getQueryParamValue } from 'src/utils/app';
import { generateProductLink } from 'src/utils/searchLinks';

interface LibraryRowProps {
  item: EnhancedLibraryRecord;
  isLast?: boolean;
  isFirst?: boolean;
  previousItemExpanded?: boolean;
  onExpandChange?: (itemId: string, expanded: boolean) => void;
}

const INITIAL_VERSIONS_SHOWN = 3;

export const LibraryRow: React.FC<LibraryRowProps> = ({
  item,
  isLast = false,
  isFirst = false,
  previousItemExpanded = false,
  onExpandChange
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const { beaconNavigator } = useBeaconNavigator();

  // Build a unified sorted list of all content items (versions, master, current)
  const allContentItems = useMemo((): ContentItem[] => {
    const items: ContentItem[] = [];

    // Add versions (use createdAt for generated date when available)
    item.enhancedVersions.forEach((version) => {
      const versionDate = version.createdAt ?? version.updatedAt;
      items.push({
        type: 'version',
        data: version,
        date: new Date(versionDate)
      });
    });

    // Add uploaded content if it exists (API may return uploadedContent or masterContent)
    const uploadedOrMasterContent = item.uploadedContent ?? item.masterContent;
    if (uploadedOrMasterContent && 'title' in uploadedOrMasterContent) {
      const masterDate = new Date(uploadedOrMasterContent.timestamp ?? 0);
      items.push({
        type: 'master',
        data: uploadedOrMasterContent,
        date: masterDate
      });
    }

    // Add current content if it exists
    if (item.currentContent) {
      const currentDate = item.currentContent.timestamp 
        ? new Date(item.currentContent.timestamp)
        : item.currentContent.updatedAt 
          ? new Date(item.currentContent.updatedAt)
          : new Date(0);
      items.push({
        type: 'current',
        data: item.currentContent,
        date: currentDate
      });
    }

    // Sort by date descending (most recent first)
    return items.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [item.enhancedVersions, item.masterContent, item.uploadedContent, item.currentContent]);

  // Determine how many items to display
  const itemsToShow = showAllVersions 
    ? allContentItems.length 
    : INITIAL_VERSIONS_SHOWN;
  const hasMoreVersions = allContentItems.length > INITIAL_VERSIONS_SHOWN;

  // Get displayed items
  const displayedItems = useMemo(
    () => allContentItems.slice(0, itemsToShow), 
    [allContentItems, itemsToShow]
  );

  // For user metadata fetching, extract user IDs from version items only
  const displayedVersions = useMemo(
    () => displayedItems.filter((i): i is ContentItem & { type: 'version' } => i.type === 'version').map(i => i.data),
    [displayedItems]
  );
  const userIds = useMemo(() => {
    const set = new Set<string>();
    displayedVersions.forEach((v) => {
      if (v.createdBy && v.createdBy !== 'content-generation-workflow') set.add(v.createdBy);
    });
    return Array.from(set);
  }, [displayedVersions]);
  const { data: userMeta } = useQuery({
    queryKey: ['libraryRowUserMeta', userIds],
    queryFn: async () => {
      const res = await fetchUserMetaData({ userIds });
      return res.data as Record<string, { firstName: string; lastName: string; email?: string }>;
    },
    enabled: userIds.length > 0,
    staleTime: 1000 * 60 * 5
  });

  const handleToggleVersions = () => {
    setShowAllVersions(!showAllVersions);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click when toggling expand
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    const itemId = item.retailerSku;
    onExpandChange?.(itemId, newExpanded);
  };

  const handleProductClick = () => {
    const mostRecentVersion = item.enhancedVersions[0];
    if (mostRecentVersion) {
      beaconNavigator.updateSearchParams({
        tab: BEACON_TABS.LIBRARY,
        subtab: BEACON_SUBTABS.LIBRARY_REVIEW,
        sku: item.retailerSku,
        contentGenId: mostRecentVersion.skuContentGenerationId,
        stacklineSku: item.stacklineSku
      });
      beaconNavigator.go();
    }
  };

  const handleVersionClick = (skuContentGenerationId: string) => {
    if (!skuContentGenerationId) return;
    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.LIBRARY,
      subtab: BEACON_SUBTABS.LIBRARY_REVIEW,
      sku: item.retailerSku,
      contentGenId: skuContentGenerationId,
      stacklineSku: item.stacklineSku
    });
    beaconNavigator.go();
  };

  // Dynamic border radius logic
  const getBorderRadius = () => {
    if (expanded) {
      return '8px 8px 0 0'; // Expanded: rounded top, flat bottom
    } else if (isFirst && isLast) {
      return '8px'; // Single row: all corners rounded
    } else if (isFirst || (previousItemExpanded && !isLast)) {
      return '8px 8px 0 0'; // First row OR after expanded row: rounded top only
    } else if (isLast) {
      return '0 0 8px 8px'; // Last row: rounded bottom only
    } else {
      return '0'; // Middle rows: no rounding
    }
  };

  // Dynamic border top logic
  const shouldHaveTopBorder = () => {
    return isFirst || previousItemExpanded;
  };

  return (
    <Box
      sx={{
        marginBottom: expanded && !isLast ? '24px' : '0px',
        transition: 'margin-bottom 0.3s ease'
      }}
    >
      {/* Main Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '60px',
          padding: '0 16px 0 16px',
          border: `1px solid ${colors.graysSecondary}`,
          borderTop: shouldHaveTopBorder() ? `1px solid ${colors.graysSecondary}` : '0',
          borderRadius: getBorderRadius(),
          backgroundColor: 'white',
          gap: '64px',
          transition: 'border-radius 0.3s ease, border-top 0.3s ease'
        }}
      >
        {/* Product Column */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            width: '429px',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
          onClick={handleProductClick}
        >
          <Box
            component="img"
            src={item.productImage}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '4px',
              objectFit: 'cover'
            }}
          />
          <Text
            variant="body2"
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: colors.darkBlue,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis'
            }}
          >
            {item.title || item.productName || 'Unknown Product'}
          </Text>
        </Box>

        {/* Retailer SKU Column */}
        <Box sx={{ width: '94px' }}>
          {(() => {
            const retailerId = getQueryParamValue('rid');
            const productLink = generateProductLink(retailerId, item.retailerSku);
            if (productLink === '#') {
              return (
                <Text
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: colors.darkBlue
                  }}
                >
                  {item.retailerSku}
                </Text>
              );
            }
            return (
              <Box
                component="a"
                href={productLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: colors.darkBlue,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {item.retailerSku}
              </Box>
            );
          })()}
        </Box>

        {/* Last Updated By Column */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '189px' }}>
          <AppProfileIcon
            size={30}
            userId={
              item.lastUpdatedBy?.userId === 'content-generation-workflow' ? undefined : item.lastUpdatedBy?.userId
            }
            initials={
              item.lastUpdatedBy?.userId === 'content-generation-workflow'
                ? 'SL'
                : getInitials(item.lastUpdatedBy?.name)
            }
            customIcon={
              item.lastUpdatedBy?.userId === 'content-generation-workflow' ? (
                <StacklineDefaultProfileImage />
              ) : undefined
            }
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
            <Text
              variant="body2"
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
                color: colors.darkBlue
              }}
            >
              {item.lastUpdatedBy?.name || 'Content Generation'}
            </Text>
            <Text
              variant="body2"
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                color: colors.grey
              }}
            >
              {item.lastUpdatedBy?.date || 'Unknown date'}
            </Text>
          </Box>
        </Box>

        {/* Versions Column */}
        <Box sx={{ flex: 1 }}>
          <Text
            variant="body2"
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              color: colors.darkBlue
            }}
          >
            {item.versions?.length || 0}
          </Text>
        </Box>

        {/* Expand/Collapse Arrow - Right Side */}
        <Box 
          sx={{ 
            width: '24px', 
            height: '24px',
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={handleToggle}
        >
          <ChevronRight
            style={{
              transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)',
              transition: 'transform 0.2s ease',
              width: '24px',
              height: '24px'
            }}
          />
        </Box>
      </Box>

      {/* Expanded Content with Collapse Animation */}
      <Collapse in={expanded} timeout={300}>
        <Box
          sx={{
            border: `1px solid ${colors.graysSecondary}`,
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            backgroundColor: 'white',
            padding: '24px 20px 24px 20px'
          }}
        >
          <Box sx={{ display: 'flex', gap: '16px' }}>
            {/* Timeline */}
            <SideDotsWithBar animateBottomDot={true} hasShowMore={hasMoreVersions} />

            {/* Content Sections */}
            <Box 
              sx={{ 
                flex: 1,
                '& > *:last-child': {
                  marginBottom: '0 !important'
                }
              }}
            >
              {/* Content Items - Versions, Master, and Current sorted by date */}
              {displayedItems.map((contentItem, index) => {
                if (contentItem.type === 'master') {
                  return <MasterContentRow key={`master-${index}`} masterContent={contentItem.data} />;
                }
                if (contentItem.type === 'current') {
                  return <CurrentContentRow key={`current-${index}`} currentContent={contentItem.data} />;
                }
                // Version item
                const version = contentItem.data;
                const versionIndex = item.enhancedVersions.findIndex(v => v.skuContentGenerationId === version.skuContentGenerationId);
                return (
                  <Box key={`version-${index}`} sx={{ marginBottom: '32px' }}>
                    {/* Version Title */}
                    <SectionTitle
                      variant="body2"
                      onClick={() => handleVersionClick(version.skuContentGenerationId)}
                      sx={{
                        cursor: 'pointer',
                        width: 'fit-content',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Version {item.enhancedVersions.length - versionIndex}
                    </SectionTitle>

                    {/* Version Details */}
                    <Box sx={{ display: 'flex' }}>
                      <DetailsRow>
                        {/* Generated By / Source */}
                        <FieldGroup width="109px">
                          <FieldLabel variant="body2">Generated By</FieldLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AppProfileIcon
                              size={24}
                              userId={version.createdBy}
                              fontSize="10px"
                              initials={getInitials(version.name)}
                              customIcon={
                                version.createdBy === 'content-generation-workflow' ? (
                                  <StacklineDefaultProfileImage />
                                ) : undefined
                              }
                            />
                            <FieldValue variant="body2">{version.name}</FieldValue>
                          </Box>
                        </FieldGroup>

                        <FieldGroup width="85px">
                          <FieldLabel variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                            Generated Date
                          </FieldLabel>
                          <FieldValue variant="body2">
                            {new Date(version.createdAt ?? version.updatedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </FieldValue>
                        </FieldGroup>

                        {/* Project Name */}
                        <FieldGroup width="109px">
                          <FieldLabel variant="body2">Project Name</FieldLabel>
                          <FieldValue variant="body2">{version.projectName || 'N/A'}</FieldValue>
                        </FieldGroup>

                        {/* Profile */}
                        <FieldGroup width="296px">
                          <FieldLabel variant="body2">Profile Name</FieldLabel>
                          {version.profileId ? (
                            <ContentProfileModal profileId={version.profileId} profileVersion={version.profileVersion}>
                              <FieldValue
                                variant="body2"
                                sx={{
                                  cursor: 'pointer',
                                  width: 'fit-content',
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {version.profileName || 'Unknown'}
                              </FieldValue>
                            </ContentProfileModal>
                          ) : (
                            <FieldValue variant="body2">{version.profileName || 'Unknown'}</FieldValue>
                          )}
                        </FieldGroup>
                      </DetailsRow>
                      {/* Status */}
                      <StatusGroup>
                        <FieldLabel variant="body2">Status</FieldLabel>
                        <FieldValue variant="body2">
                          {version.libraryStatus 
                            ? version.libraryStatus.charAt(0).toUpperCase() + version.libraryStatus.slice(1).toLowerCase()
                            : 'Unknown'}
                        </FieldValue>
                      </StatusGroup>
                    </Box>
                  </Box>
                );
              })}

              {/* View more/less for versions */}
              {hasMoreVersions && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    marginTop: '8px'
                  }}
                >
                 {/* View more text */}
                  <Text
                    variant="body2"
                    onClick={handleToggleVersions}
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '18px',
                      color: colors.darkBlue,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  >
                    {showAllVersions ? 'View less' : 'View more'}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
